import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  BoltIcon,
  BoltSlashIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { SaveChanges } from 'components/SaveChanges'
import {
  canContractorOriginHaveSection,
  ContractorFieldProps,
  ContractorInputType,
  ContractorOrigin,
  contractorOriginOptions,
  ensureContractorSections,
  getContractorSectionOptions,
  IContractorOriginSection,
  InputType,
  IVisibleProp,
} from 'config'
import { getVisibleLogics2Text } from 'config/loan.input.fields.constants'
import { useEffect, useMemo, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Section, Tooltip } from 'stories/components'
import { confirm, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { ContractorInputPropertyModal } from './ContractorInputPropertyModal'
import { EditableOriginTitle } from './EditableOriginTitle'

const CONFIG_KEY = 'contractorFieldProperties'

export const ContractorFieldProperties = (props: { setChanged: Function }) => {
  const { setChanged } = props

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ContractorFieldProps>()
  const [isChanged, setIsChanged] = useState<boolean | undefined>(undefined)
  const [selectedField, setSelectedField] = useState<{
    origin: ContractorOrigin
    fieldKey: string
    defaultFieldData?: Record<string, any>
  } | null>(null)
  const [orderPageIndex, setOrderPageIndex] = useState<{ origin: ContractorOrigin; sectionId: number } | null>(null)

  useEffect(() => {
    setLoading(true)
    getAdminConfig(CONFIG_KEY)
      .then((res) => {
        setData(ensureContractorSections(res as ContractorFieldProps))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (isChanged === undefined) return
    setChanged(isChanged)
  }, [isChanged])

  const markChanged = (next: ContractorFieldProps) => {
    setData(next)
    setIsChanged(true)
  }

  const onChangeValue = (origin: ContractorOrigin, fieldKey: string, type: string, value: any) => {
    setData((prev: ContractorFieldProps | undefined) => {
      const next = cloneDeep(prev) as ContractorFieldProps
      if (!next[origin]) next[origin] = { inputs: {} }
      if (!next[origin]?.inputs[fieldKey]) next[origin].inputs[fieldKey] = {}
      ;(next[origin].inputs[fieldKey] as any)[type] = value
      return next
    })
    setIsChanged(true)
  }

  const onClosePropertyModal = (updated?: ContractorInputType) => {
    if (!selectedField) return
    const { origin, fieldKey: prevKey, defaultFieldData } = selectedField
    setSelectedField(null)
    if (!updated || !data) return

    const newData = cloneDeep(data)
    if (!newData[origin]) newData[origin] = { inputs: {} }

    let fieldKey = prevKey
    const isNew = !fieldKey
    if (isNew) {
      fieldKey = `${Date.now()}`
      if (updated.required === undefined) updated.required = true
      if (!updated.section && defaultFieldData?.section) updated.section = defaultFieldData.section
    }

    const previousSection = isNew
      ? defaultFieldData?.section
      : newData[origin].inputs[fieldKey]?.section || defaultFieldData?.section

    newData[origin].inputs[fieldKey] = { ...newData[origin].inputs[fieldKey], ...updated }

    if (canContractorOriginHaveSection(origin)) {
      if (!newData[origin].sections) newData[origin].sections = []
      const sections = newData[origin].sections!

      if (`${previousSection || ''}` !== `${updated.section || ''}`) {
        if (previousSection) {
          const oldIdx = sections.findIndex((v) => `${v.id}` === `${previousSection}`)
          if (oldIdx !== -1) {
            const sIndex = sections[oldIdx].fields.indexOf(fieldKey)
            if (sIndex !== -1) sections[oldIdx].fields.splice(sIndex, 1)
          }
        }
        if (updated.section) {
          const newIdx = sections.findIndex((v) => `${v.id}` === `${updated.section}`)
          if (newIdx !== -1 && !sections[newIdx].fields.includes(fieldKey)) {
            sections[newIdx].fields.push(fieldKey)
          }
        }
      } else if (isNew && updated.section) {
        const newIdx = sections.findIndex((v) => `${v.id}` === `${updated.section}`)
        if (newIdx !== -1 && !sections[newIdx].fields.includes(fieldKey)) {
          sections[newIdx].fields.push(fieldKey)
        }
      }
    }

    markChanged(newData)
  }

  const onRemoveField = async (origin: ContractorOrigin, fieldKey: string) => {
    if (!data) return
    const isContinue = await confirm('Are you sure you want to remove this field?')
    if (!isContinue) return

    const newData = cloneDeep(data)
    delete newData[origin].inputs[fieldKey]
    newData[origin].sections?.forEach((section) => {
      const idx = section.fields.indexOf(fieldKey)
      if (idx !== -1) section.fields.splice(idx, 1)
    })
    markChanged(newData)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig(CONFIG_KEY, data ?? {})
    toast('Saved successfully', { type: 'success' })
    setIsChanged(false)
    setLoading(false)
  }

  const contractorFieldInputs = useMemo(() => {
    if (!data) return null
    const result: Record<string, Record<string, InputType>> = {}
    Object.values(ContractorOrigin).forEach((origin) => {
      result[origin] = (data[origin]?.inputs ?? {}) as Record<string, InputType>
    })
    return result
  }, [data])

  const orderingSectionInputs = useMemo(() => {
    if (!orderPageIndex || !data) return []
    const { origin, sectionId } = orderPageIndex
    const section = data[origin]?.sections?.find((v) => v.id === sectionId)
    if (!section) return []
    return section.fields.map((fieldKey) => {
      const field = data[origin].inputs[fieldKey]
      return {
        fieldKey,
        title: field?.title || fieldKey,
      }
    })
  }, [orderPageIndex, data])

  const onCloseSectionOrderModal = (newInputs: any[] | null) => {
    if (!orderPageIndex) return
    if (!newInputs || !data) {
      setOrderPageIndex(null)
      return
    }
    const { origin, sectionId } = orderPageIndex
    const index = data[origin]?.sections?.findIndex((v) => v.id === sectionId)
    const fields = newInputs.map((v) => v.fieldKey)
    const newData = cloneDeep(data)
    if (index !== undefined && index !== -1 && newData[origin]?.sections?.[index]) {
      newData[origin].sections![index].fields = fields
    }
    markChanged(newData)
    setOrderPageIndex(null)
  }

  const isCustomFieldKey = (fieldKey: string) => `${Number(fieldKey)}` === fieldKey

  const renderInput = (origin: ContractorOrigin, fieldKey: string) => {
    const inputProps = data?.[origin]?.inputs?.[fieldKey]
    if (!inputProps) return null

    const input: InputType = {
      ...inputProps,
      value: inputProps.defaultValue,
      history: false,
    } as any

    const visible = inputProps.visible !== false
    const required = inputProps.required !== undefined ? !!inputProps.required : true
    const RequiredIcon = required ? BoltIcon : BoltSlashIcon
    const visibleLogicText = getVisibleLogics2Text(
      inputProps as IVisibleProp,
      contractorOriginOptions as any,
      contractorFieldInputs as any,
    )

    return (
      <div key={fieldKey} className={`col-span-${input.span || 1} ${visible ? 'opacity-100' : 'opacity-30 z-10'}`}>
        <RenderInput
          Key={`${origin}-${fieldKey}`}
          input={{
            ...(input as InputType),
            required,
            additionalElements: (
              <div className="flex">
                <Tooltip message={visibleLogicText || (visible ? 'Visible' : 'Hidden')}>
                  <span className="btn-icon" onClick={() => onChangeValue(origin, fieldKey, 'visible', !visible)}>
                    {visible ? (
                      <EyeIcon className={`w-4 h-4 ${!visibleLogicText && 'text-gray-500'}`} />
                    ) : (
                      <EyeSlashIcon className={`w-4 h-4 ${!visibleLogicText && 'text-gray-500'}`} />
                    )}
                  </span>
                </Tooltip>
                <Tooltip message="Edit field properties">
                  <span className="btn-icon" onClick={() => setSelectedField({ origin, fieldKey })}>
                    <PencilSquareIcon className="w-4 h-4 text-gray-500" />
                  </span>
                </Tooltip>
                <Tooltip message={required ? 'Required' : 'Not Required'}>
                  <span className="btn-icon" onClick={() => onChangeValue(origin, fieldKey, 'required', !required)}>
                    <RequiredIcon className="w-4 h-4 text-gray-500" />
                  </span>
                </Tooltip>
                {isCustomFieldKey(fieldKey) && (
                  <Tooltip message="Remove field">
                    <span className="btn-icon" onClick={() => onRemoveField(origin, fieldKey)}>
                      <TrashIcon className="w-4 h-4 text-red-500" />
                    </span>
                  </Tooltip>
                )}
              </div>
            ),
          }}
          onChange={(_key: string, value: any) => onChangeValue(origin, fieldKey, 'defaultValue', value)}
        />
      </div>
    )
  }

  const getSectionFields = (origin: ContractorOrigin, sectionId: number) => {
    return data?.[origin]?.sections?.find((v) => v.id === sectionId)?.fields || []
  }

  const getAllSectionFields = (origin: ContractorOrigin) => {
    const all: string[] = []
    data?.[origin]?.sections?.forEach((v) => all.push(...v.fields))
    return all
  }

  const renderSectionFields = (origin: ContractorOrigin, sectionId: number | null) => {
    if (!data) return null
    const inputs = data[origin]?.inputs ?? {}
    const sectionFieldKeys = sectionId ? getSectionFields(origin, sectionId) : getAllSectionFields(origin)
    const keys = sectionId
      ? sectionFieldKeys
      : Object.keys(inputs).filter((key) => !sectionFieldKeys.includes(key))

    if (!keys.length && sectionId == null) return null

    return (
      <>
        {sectionId == null && !!keys.length && canContractorOriginHaveSection(origin) && (
          <Section title="No Section" />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
          {keys.map((fieldKey) => renderInput(origin, fieldKey))}
        </div>
      </>
    )
  }

  const onAddNewSection = (origin: ContractorOrigin) => {
    if (!data) return
    const newData = cloneDeep(data)
    if (!newData[origin].sections) newData[origin].sections = []
    newData[origin].sections!.push({
      id: Date.now(),
      title: `Section ${newData[origin].sections!.length + 1}`,
      fields: [],
    })
    markChanged(newData)
  }

  const onAddNewField = (origin: ContractorOrigin, defaultFieldData: Record<string, any> = { section: '' }) => {
    setSelectedField({ origin, fieldKey: '', defaultFieldData })
  }

  const renderOriginSections = (origin: ContractorOrigin) => {
    if (!data) return null
    const sections: IContractorOriginSection[] = data[origin]?.sections || []

    const onUpdateSectionTitle = async (section: IContractorOriginSection, index: number) => {
      const title: any = await prompt('Input section title', { value: section.title })
      if (title === false) return
      const newData = cloneDeep(data)
      newData[origin].sections![index].title = title
      markChanged(newData)
    }

    const onRemoveSection = async (section: IContractorOriginSection, index: number) => {
      const fieldCount = section.fields?.length || 0
      const isContinue = await confirm(
        fieldCount
          ? `Are you sure you want to remove this section and its ${fieldCount} field${fieldCount === 1 ? '' : 's'}?`
          : 'Are you sure you want to remove this section?',
      )
      if (!isContinue) return

      const newData = cloneDeep(data)
      const sections = newData[origin].sections
      if (!sections?.length) return

      const removed = sections.splice(index, 1)[0]
      ;(removed?.fields || []).forEach((fieldKey) => {
        delete newData[origin].inputs[fieldKey]
      })

      markChanged(newData)
    }

    const onUpdateOrder = (index: number, direction: number) => {
      const newData = cloneDeep(data)
      const list = newData[origin].sections!
      list.splice(index + direction, 0, list.splice(index, 1)[0])
      markChanged(newData)
    }

    if (!canContractorOriginHaveSection(origin)) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">{Object.keys(data[origin]?.inputs ?? {}).map((key) => renderInput(origin, key))}</div>
    }

    return (
      <div>
        {sections.map((section, index) => (
          <div key={section.id} className="mb-4">
            <RenderInput
              Key={`section-${origin}-${section.id}`}
              input={{
                inputType: 'section',
                title: section.title || '',
                additionalElements: (
                  <div className="flex gap-2">
                    <Tooltip message="Add a new field in this section">
                      <span
                        className="btn-icon"
                        onClick={() => onAddNewField(origin, { section: `${section.id}` })}
                      >
                        <PlusIcon className="w-4 h-4" />
                      </span>
                    </Tooltip>
                    <p className="btn-icon" onClick={() => setOrderPageIndex({ origin, sectionId: section.id })}>
                      <BarsArrowDownIcon className="w-4 h-4" />
                    </p>
                    {index !== 0 && (
                      <span className="btn-icon" onClick={() => onUpdateOrder(index, -1)}>
                        <ArrowUpIcon className="w-4 h-4" />
                      </span>
                    )}
                    {index !== sections.length - 1 && (
                      <span className="btn-icon" onClick={() => onUpdateOrder(index, 1)}>
                        <ArrowDownIcon className="w-4 h-4" />
                      </span>
                    )}
                    <span className="btn-icon" onClick={() => onUpdateSectionTitle(section, index)}>
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span className="btn-icon !text-red-500" onClick={() => onRemoveSection(section, index)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                ),
              }}
              onChange={() => {}}
            />
            {renderSectionFields(origin, section.id)}
          </div>
        ))}

        {renderSectionFields(origin, null)}

        <div className="flex items-center gap-4">
          <Button link onClick={() => onAddNewSection(origin)}>
            + Add New Section
          </Button>
          <Button link onClick={() => onAddNewField(origin)}>
            + Add New Field
          </Button>
        </div>
      </div>
    )
  }

  const renderOrigin = (origin: ContractorOrigin, pageIdx: number) => {
    const title = data?.[origin]?.title ?? contractorOriginOptions[origin]

    const onTitleUpdate = (nextTitle: string) => {
      if (!data) return
      const newData = cloneDeep(data)
      newData[origin].title = nextTitle
      markChanged(newData)
    }

    return (
      <div key={origin} className="mb-4">
        <p className="text-lg font-bold flex items-center gap-2 mb-2">
          {pageIdx + 1}.
          <EditableOriginTitle value={title} onUpdate={onTitleUpdate} />
        </p>
        {renderOriginSections(origin)}
      </div>
    )
  }

  const originList = Object.values(ContractorOrigin)

  return (
    <div className="contractor-field-properties relative">
      <LayoutLoading show={loading} />
      <Prompt when={!!isChanged} message="Are you sure you want to leave without saving your changes?" />

      {data && (
        <>
          <div>{originList.map((origin, index) => renderOrigin(origin, index))}</div>
          <SaveChanges show={!!isChanged} label="Save Changes" onSave={onSubmit} />
        </>
      )}

      {selectedField && data && (
        <ContractorInputPropertyModal
          fieldKey={selectedField.fieldKey}
          fieldData={{
            ...(data[selectedField.origin]?.inputs?.[selectedField.fieldKey] ?? {}),
            ...(selectedField.defaultFieldData || {}),
          }}
          data={data}
          sectionOptions={
            canContractorOriginHaveSection(selectedField.origin)
              ? getContractorSectionOptions(data[selectedField.origin]?.sections)
              : undefined
          }
          onClose={onClosePropertyModal}
        />
      )}

      {orderPageIndex && (
        <PageOrderModal key="sectionOrder" inputs={orderingSectionInputs} onClose={onCloseSectionOrderModal} />
      )}
    </div>
  )
}
